import React, { FC, ReactNode } from 'react';

import { Icon as KitIcon } from '@column/column-ui-kit';
import { Icon, Tooltip, TooltipContent, TooltipTrigger, cn } from '@column/ui';

export const TRAILING_STATUS_ADDON_CLASS_NAME =
  'cursor-default gap-3 has-[>button]:mr-0 has-[>button]:pr-2 has-[>span>button]:mr-0 has-[>span>button]:pr-2 has-[>[data-slot=status-or-action]]:mr-0 has-[>[data-slot=status-or-action]]:pr-2';

const REVEAL_CLASS_NAME =
  'group-hover/input-group:visible group-hover/input-group:opacity-100 group-focus-within/input-group:visible group-focus-within/input-group:opacity-100';
const CONCEAL_CLASS_NAME =
  'group-hover/input-group:invisible group-hover/input-group:opacity-0 group-focus-within/input-group:invisible group-focus-within/input-group:opacity-0';

interface StatusOrActionProps {
  status: ReactNode;
  action?: ReactNode;
}

export const StatusOrAction: FC<StatusOrActionProps> = ({ status, action }) => {
  if (!action) {
    return <>{status}</>;
  }

  return (
    <div data-slot="status-or-action" className="grid items-center justify-items-end *:col-start-1 *:row-start-1">
      <div className={cn('pr-1 transition-[opacity,visibility] duration-150', CONCEAL_CLASS_NAME)}>{status}</div>
      <div className={cn('invisible flex opacity-0 transition-[opacity,visibility] duration-150', REVEAL_CLASS_NAME)}>
        {action}
      </div>
    </div>
  );
};

export type FieldStatusTone = 'success' | 'warning' | 'pending';

export type FieldStatusSize = 'sm' | 'xs';

const SIZE_CLASS_NAMES: Record<FieldStatusSize, { text: string; icon: string }> = {
  sm: { text: 'text-sm', icon: '[--icon-size-x:16px] [--icon-size-y:16px]' },
  xs: { text: 'text-xs', icon: '[--icon-size-x:14px] [--icon-size-y:14px]' },
};

const toneIcon = (tone: FieldStatusTone, size: FieldStatusSize): ReactNode => {
  const iconClassName = SIZE_CLASS_NAMES[size].icon;
  switch (tone) {
    case 'success':
      return <KitIcon.Checkmark className={cn(iconClassName, 'text-seafoam-600 dark:text-seafoam-400')} />;
    case 'warning':
      return <KitIcon.Warning className={cn(iconClassName, 'text-orange-500')} />;
    case 'pending':
      return <Icon.Pending className={cn('animate-[spin_4s_linear_infinite]', size === 'xs' && 'size-3.5')} />;
  }
};

interface FieldStatusProps {
  tone: FieldStatusTone;
  label: string;
  tooltip?: string;
  size?: FieldStatusSize;
}

export const FieldStatus: FC<FieldStatusProps> = ({ tone, label, tooltip, size = 'sm' }) => {
  const status = (
    <span
      key={tone}
      data-cy="field-status"
      className={cn(
        'flex animate-in items-center gap-1 font-medium whitespace-nowrap text-gray-500 duration-200 fade-in slide-in-from-top-1 dark:text-gray-400',
        SIZE_CLASS_NAMES[size].text
      )}
    >
      {label}
      {toneIcon(tone, size)}
    </span>
  );

  if (!tooltip) {
    return status;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{status}</TooltipTrigger>
      <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
};
